import { Carousel } from "@/components/Carousel";
import { cn } from "@/lib/utils";
import {
  applyEvolutionStep,
  evolutionBuilderPlayerFiltersSelector,
  evolutionsBuilderSelector,
  fetchEvolutionSteps,
  getCurrentEvolutionPlayerSelector,
} from "@/redux/evolutions/evolutionsBuilderSlice";
import { evolutionsSelector } from "@/redux/evolutions/evolutionsSlice";
import { AppDispatch } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import { groupStepsByRewardCategory } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { type EvolutionStep, type RewardCategory } from "@repo/shared/types";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { EvoStepCard } from "../EvolutionsBuilder/components/EvoStepCard";
import { EvolutionsStepsSkeleton } from "./EvolutionsStepsSkeleton";

// The reward category strings double as ids in the slice; display goes
// through this lookup and falls back to the raw string for unknown ones.
const REWARD_CATEGORY_KEYS = {
  "Visual Upgrades": "rewardCategories.visualUpgrades",
  "Stats and In-game Attributes": "rewardCategories.statsAndInGameAttributes",
  Positions: "rewardCategories.positions",
  "Skill Moves and Weak Foot": "rewardCategories.skillMovesAndWeakFoot",
  "Player Roles": "rewardCategories.playerRoles",
  PlayStyles: "rewardCategories.playStyles",
  "Small Evolutions": "rewardCategories.smallEvolutions",
  "Mixed Upgrades": "rewardCategories.mixedUpgrades",
} as const satisfies Record<RewardCategory, string>;

const categoryLayout = (
  category: RewardCategory,
): {
  size: "small" | "medium" | "large" | "chain";
  customClassNames: string;
} => {
  if (category === "Visual Upgrades") {
    return { size: "large", customClassNames: "grid-cols-2" };
  }
  if (category === "Mixed Upgrades" || category === "Player Roles") {
    return {
      size: "medium",
      customClassNames: "grid-cols-3 large-desktop:grid-cols-4",
    };
  }
  return {
    size: "small",
    customClassNames: "grid-cols-3 large-desktop:grid-cols-4",
  };
};

export const EvolutionsSteps = ({
  onClickStep,
  evolutionSteps,
  currentEvolutionChain,
  loading,
  playerRoleIdsChosenPlayer,
  searchTerm,
}: {
  onClickStep: (step: EvolutionStep) => void;
  evolutionSteps: EvolutionStep[];
  currentEvolutionChain: number[];
  loading: boolean;
  playerRoleIdsChosenPlayer: number[];
  searchTerm: string;
}) => {
  const { t } = useTranslation("evoBuilder");
  const dispatch = useDispatch<AppDispatch>();
  const { stepStatus, initialEvolutionSteps, includedEvolutions } = useSelector(
    evolutionsBuilderSelector,
  );
  const { evolutions, expiredEvolutions } = useSelector(evolutionsSelector);
  // Steps whose evolution is unknown have no category to sit under.
  const sections = useMemo(
    () =>
      groupStepsByRewardCategory(
        evolutionSteps,
        evolutions,
        expiredEvolutions,
        searchTerm,
      ).filter((section) => section.category),
    [evolutionSteps, evolutions, expiredEvolutions, searchTerm],
  );
  const evolutionBuilderFilters = useSelector(
    evolutionBuilderPlayerFiltersSelector,
  );
  const currentEvolutionPlayer = useSelector(getCurrentEvolutionPlayerSelector);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  const handleStepClick = (step: EvolutionStep) => {
    onClickStep(step);
  };

  useEffect(() => {
    dispatch(fetchEvolutionSteps({ playerRoleIds: playerRoleIdsChosenPlayer }));
  }, [
    currentEvolutionChain.length,
    playerRoleIdsChosenPlayer,
    includedEvolutions,
    evolutionBuilderFilters,
  ]);

  // Show skeleton on first load (pending status and no initial steps)
  const showSkeleton =
    stepStatus === "pending" && initialEvolutionSteps.length === 0;

  if (showSkeleton) {
    return <EvolutionsStepsSkeleton />;
  }

  return (
    <div className="flex flex-col gap-4 pb-8">
      {sections.map(({ category, data: evolutionStepsInCategory }) => {
        const { size, customClassNames } = categoryLayout(category);
        const categoryKey = REWARD_CATEGORY_KEYS[category];
        const categoryHeading = t("categoryCount", {
          category: categoryKey ? t(categoryKey) : category,
          count: evolutionStepsInCategory.length,
        });

        const evoCards = evolutionStepsInCategory.map((step) => (
          // Inside the phone carousel a card would take its widest content;
          // pin it to the viewport so its rows wrap instead.
          <div
            key={step.evolutionId + "-" + step}
            className={cn(isMobile && "w-[calc(100vw-3rem)] max-w-sm")}
          >
            <EvoStepCard
              {...step}
              size={size}
              player={
                category === "Visual Upgrades"
                  ? !step.disabled
                    ? applyEvolutionStep(currentEvolutionPlayer, step)
                    : currentEvolutionPlayer
                  : null
              }
              metaRatingBeforeApplyingUpgrade={
                currentEvolutionPlayer?.metaRating
              }
              playerRoleIdBeforeApplyingUpgrade={
                currentEvolutionPlayer?.playerRoleId
              }
              loading={loading}
              onClick={() => handleStepClick(step)}
            />
          </div>
        ));

        if (isMobile) {
          return (
            <Carousel
              key={category}
              header={
                <h3 className="text-sm font-semibold text-foreground-muted">
                  {categoryHeading}
                </h3>
              }
              items={evoCards}
              gap="gap-2"
              className="mt-6"
            />
          );
        }

        return (
          <div className="mt-6 flex flex-col gap-3" key={category}>
            <h3 className="text-sm font-semibold text-foreground-muted">
              {categoryHeading}
            </h3>
            <div className={cn("grid auto-rows-fr gap-4", customClassNames)}>
              {evoCards}
            </div>
          </div>
        );
      })}
    </div>
  );
};
